/**
 * Key detection + scale-aware harmony snapping.
 *
 * Pipeline:
 *   1. `detectKey(buffer)` — Krumhansl-Schmuckler key-finding on a chroma
 *      vector derived from the audio. Returns { tonic, mode, confidence }.
 *   2. `snapToScale(offsets, tonicPitchClass, mode)` — takes the preset's
 *      desired interval offsets and snaps each to the nearest in-scale
 *      semitone. Lead (offset 0) is never moved; other voices nudge ±1 st
 *      to fit the scale, preserving relative pitch direction.
 *
 * Why this matters: a "+4 st" harmony is a major 3rd. In a minor key, the
 * in-scale 3rd is a MINOR 3rd (+3 st). Without snapping, gospel-choir
 * harmonies sound wrong on a sad song — they introduce out-of-key notes.
 *
 * Algorithm is intentionally tiny (~100 lines), runs in <30 ms on a
 * 90-second song. No external deps.
 */

/** Krumhansl-Kessler key profiles. Each row is the relative perceived
 *  importance of each pitch class for that key. Values from the original
 *  1982 paper, normalised so each profile has sum ~12. */
const KK_MAJOR = [6.35, 2.23, 3.48, 2.33, 4.38, 4.09, 2.52, 5.19, 2.39, 3.66, 2.29, 2.88];
const KK_MINOR = [6.33, 2.68, 3.52, 5.38, 2.60, 3.53, 2.54, 4.75, 3.98, 2.69, 3.34, 3.17];

const NOTE_NAMES = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];

/** Compute a 12-bin chroma vector from an AudioBuffer. Sums spectral
 *  energy per pitch class across overlapping ~93 ms windows. */
function chromaVector(buffer) {
  const sr = buffer.sampleRate;
  // Average down to mono once so we only FFT one channel.
  const ch0 = buffer.getChannelData(0);
  const ch1 = buffer.numberOfChannels > 1 ? buffer.getChannelData(1) : ch0;
  const samples = ch0.length;
  const mono = new Float32Array(samples);
  for (let i = 0; i < samples; i++) mono[i] = (ch0[i] + ch1[i]) * 0.5;

  // 4096-sample window (~92 ms @ 44.1 kHz). Hop = window / 2 for 50%
  // overlap — gives enough temporal density on 60 s song to be robust to
  // momentary noise while finishing in <30 ms.
  const winSize = 4096;
  const hop = winSize / 2;
  const chroma = new Float32Array(12);
  const fft = makeFft(winSize);

  // Hann window precomputed once.
  const window = new Float32Array(winSize);
  for (let i = 0; i < winSize; i++) window[i] = 0.5 * (1 - Math.cos((2 * Math.PI * i) / (winSize - 1)));

  const real = new Float32Array(winSize);
  const imag = new Float32Array(winSize);
  for (let start = 0; start + winSize < samples; start += hop) {
    for (let i = 0; i < winSize; i++) {
      real[i] = mono[start + i] * window[i];
      imag[i] = 0;
    }
    fft(real, imag);
    // Only the lower-half bins are meaningful (Nyquist).
    for (let k = 1; k < winSize / 2; k++) {
      const mag = Math.sqrt(real[k] * real[k] + imag[k] * imag[k]);
      const freq = (k * sr) / winSize;
      if (freq < 60 || freq > 2000) continue; // skip sub-bass and high noise
      // Map frequency to pitch class.
      const midi = Math.round(12 * Math.log2(freq / 440) + 69);
      const pc = ((midi % 12) + 12) % 12;
      chroma[pc] += mag;
    }
  }
  // Normalise.
  let sum = 0;
  for (let i = 0; i < 12; i++) sum += chroma[i];
  if (sum > 0) for (let i = 0; i < 12; i++) chroma[i] /= sum;
  return chroma;
}

/** Tiny Cooley-Tukey radix-2 in-place FFT. Input length must be a power
 *  of 2. Mutates the real + imag arrays. No external dependency. */
function makeFft(N) {
  // Precompute bit-reversal indices.
  const rev = new Uint32Array(N);
  for (let i = 0; i < N; i++) {
    let j = 0, n = i;
    for (let k = 1; k < N; k <<= 1) { j = (j << 1) | (n & 1); n >>= 1; }
    rev[i] = j;
  }
  // Precompute twiddles for each stage.
  return function fft(real, imag) {
    for (let i = 0; i < N; i++) {
      const j = rev[i];
      if (j > i) {
        [real[i], real[j]] = [real[j], real[i]];
        [imag[i], imag[j]] = [imag[j], imag[i]];
      }
    }
    for (let size = 2; size <= N; size <<= 1) {
      const half = size >> 1;
      const ang = -2 * Math.PI / size;
      const wpr = Math.cos(ang);
      const wpi = Math.sin(ang);
      for (let start = 0; start < N; start += size) {
        let wr = 1, wi = 0;
        for (let k = 0; k < half; k++) {
          const i1 = start + k;
          const i2 = i1 + half;
          const tr = wr * real[i2] - wi * imag[i2];
          const ti = wr * imag[i2] + wi * real[i2];
          real[i2] = real[i1] - tr;
          imag[i2] = imag[i1] - ti;
          real[i1] += tr;
          imag[i1] += ti;
          const newWr = wr * wpr - wi * wpi;
          wi = wr * wpi + wi * wpr;
          wr = newWr;
        }
      }
    }
  };
}

/** Pearson correlation between two equal-length arrays. */
function correlate(a, b) {
  const n = a.length;
  let sa = 0, sb = 0;
  for (let i = 0; i < n; i++) { sa += a[i]; sb += b[i]; }
  const ma = sa / n, mb = sb / n;
  let num = 0, da = 0, db = 0;
  for (let i = 0; i < n; i++) {
    const va = a[i] - ma;
    const vb = b[i] - mb;
    num += va * vb;
    da += va * va;
    db += vb * vb;
  }
  return da > 0 && db > 0 ? num / Math.sqrt(da * db) : 0;
}

/** Detect the key of an AudioBuffer.
 *  @returns {{ tonic: number, mode: 'major'|'minor', confidence: number, name: string }}
 *  `tonic` is a pitch class 0-11 (0 = C). `confidence` is the Pearson
 *  correlation of the best-matching profile (0..1). */
export function detectKey(buffer) {
  const chroma = chromaVector(buffer);
  let best = { tonic: 0, mode: "major", correlation: -1 };
  for (let t = 0; t < 12; t++) {
    // Rotate the chroma so the candidate tonic sits at index 0.
    const rotated = new Array(12);
    for (let i = 0; i < 12; i++) rotated[i] = chroma[(i + t) % 12];
    const corrMaj = correlate(rotated, KK_MAJOR);
    const corrMin = correlate(rotated, KK_MINOR);
    if (corrMaj > best.correlation) best = { tonic: t, mode: "major", correlation: corrMaj };
    if (corrMin > best.correlation) best = { tonic: t, mode: "minor", correlation: corrMin };
  }
  return {
    tonic: best.tonic,
    mode: best.mode,
    confidence: Math.max(0, best.correlation),
    name: `${NOTE_NAMES[best.tonic]} ${best.mode}`,
  };
}

/** Scale degrees for major + (natural) minor, as semitone offsets from
 *  the tonic. */
const MAJOR_SCALE = [0, 2, 4, 5, 7, 9, 11];
const MINOR_SCALE = [0, 2, 3, 5, 7, 8, 10];

/** Find the closest in-scale semitone to a target offset, given the
 *  song's tonic + mode. `offset` is relative to the lead vocal's pitch
 *  (not the tonic). We approximate the lead as the tonic for snapping
 *  (good enough for harmony intervals — the listener's ear locks onto
 *  intervals, not absolute pitch).
 *
 *  Returns the offset rounded to the nearest scale degree above the lead. */
export function snapOffsetToScale(offset, mode = "major") {
  if (offset === 0) return 0;                  // never move the lead
  if (Math.abs(offset) >= 24) return offset;   // ±2 octaves+ pass-through
  const scale = mode === "minor" ? MINOR_SCALE : MAJOR_SCALE;
  // Build a chromatic list of in-scale offsets across [-12, +12].
  const inScale = [];
  for (const oct of [-12, 0, 12]) {
    for (const s of scale) inScale.push(oct + s);
  }
  // Find nearest by absolute distance, preferring the same direction.
  let best = inScale[0];
  let bestDist = Infinity;
  for (const v of inScale) {
    const d = Math.abs(v - offset);
    if (d < bestDist) { bestDist = d; best = v; }
  }
  return best;
}

/** Snap an entire harmony-stack to the song's scale. Mutation-free —
 *  returns a fresh array. */
export function snapStackToKey(voices, key) {
  if (!key || !key.mode) return voices;
  return voices.map((v) => ({
    ...v,
    offset: snapOffsetToScale(v.offset, key.mode),
  }));
}
